import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { LayoutDashboard, Users, Clock3, LogOut, Menu, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useHR } from "@/lib/hr-store";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffect, useMemo, useState } from "react";
import {
  loginWithRemoteApi,
  logoutLocalAccount,
  markEmployeeStepDone,
  useAuth,
} from "@/lib/local-auth";

const navTop = [{ to: "/", label: "Dashboard", icon: LayoutDashboard }];

const navEmployee = [
  { to: "/karyawan", label: "Karyawan", icon: Users },
];

export default function AppShell() {
  const navigate = useNavigate();
  const loc = useLocation();
  const authUserId = useAuth((s) => s.currentUserId);
  const accounts = useAuth((s) => s.accounts);
  const currentUser = accounts.find((account) => account.id === authUserId) || null;
  const employeeStepDone = !!currentUser?.employeeStepDone;
  const employees = useHR((s) => s.employees);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [openMobileNav, setOpenMobileNav] = useState(false);
  const [openEmployeeNav, setOpenEmployeeNav] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    if (!employeeStepDone && loc.pathname !== "/karyawan") {
      navigate({ to: "/karyawan", replace: true });
    }
  }, [currentUser, employeeStepDone, loc.pathname, navigate]);

  useEffect(() => {
    const shouldOpenEmployee = loc.pathname === "/karyawan";
    if (shouldOpenEmployee) setOpenEmployeeNav(true);
  }, [loc.pathname]);

  const activeOnboardingPath = useMemo(() => {
    if (!currentUser) return null;
    if (!employeeStepDone) return "/karyawan";
    return null;
  }, [currentUser, employeeStepDone]);

  const renderNavLink = (n: { to: string; label: string; icon: typeof LayoutDashboard }, mobile = false) => {
    const active = loc.pathname === n.to;
    const Icon = n.icon;
    const lockedByOnboarding = !!activeOnboardingPath && activeOnboardingPath !== n.to;
    const disabled = lockedByOnboarding;
    return (
      <div key={n.to} className={disabled ? "opacity-50 pointer-events-none" : ""}>
        <Link
          to={n.to}
          onClick={() => {
            if (mobile) setOpenMobileNav(false);
          }}
          className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition ${
            active
              ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
              : "text-sidebar-foreground hover:bg-sidebar-accent/60"
          }`}
        >
          <Icon className="h-4 w-4" />
          {n.label}
        </Link>
      </div>
    );
  };

  const renderNavLinks = (mobile = false) => (
    <div className="space-y-3">
      <div className="space-y-1">{navTop.map((n) => renderNavLink(n, mobile))}</div>

      <div className="pt-1">
        <button
          type="button"
          onClick={() => setOpenEmployeeNav((v) => !v)}
          className="w-full flex items-center justify-between rounded-md px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent/60 transition"
        >
          <span className="flex items-center gap-3">
            <Users className="h-4 w-4" />
            Data Karyawan
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${openEmployeeNav ? "rotate-180" : ""}`} />
        </button>
        {openEmployeeNav && (
          <div className="mt-1 pl-3 space-y-1">
            {navEmployee.map((n) => renderNavLink(n, mobile))}
          </div>
        )}
      </div>

      <div className="pt-1">
        <div
          className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-sidebar-accent/60 transition ${
            loc.pathname === "/periode" || loc.pathname.startsWith("/periode/")
              ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
              : "text-sidebar-foreground"
          }`}
        >
          <Link
            to="/periode"
            onClick={() => {
              if (mobile) setOpenMobileNav(false);
            }}
            className="flex items-center gap-3 flex-1"
          >
            <Clock3 className="h-4 w-4" />
            Periode Penggajian
          </Link>
        </div>
      </div>
    </div>
  );

  // Periode aktif (YYYY-MM) masih disimpan di local state untuk kalkulasi payroll lama.

  const handleMarkEmployeeStepDone = () => {
    if (employees.length === 0) {
      toast.error("Tambahkan minimal 1 karyawan untuk lanjut onboarding");
      return;
    }
    markEmployeeStepDone();
    toast.success("Tahap Data Karyawan selesai");
  };

  const submitLogin = async () => {
    if (authBusy) return;
    setAuthBusy(true);
    try {
      const result = await loginWithRemoteApi(authEmail, authPassword);
      if (!result.ok) {
        toast.error(result.message || "Autentikasi gagal");
        return;
      }
      setAuthPassword("");
      toast.success("Berhasil login");
    } finally {
      setAuthBusy(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-lg">Masuk ke Payroll Dashboard</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input value={authEmail} onChange={(event) => setAuthEmail(event.target.value)} placeholder="nama@perusahaan.com" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Password</Label>
                <Input
                  type="password"
                  value={authPassword}
                  onChange={(event) => setAuthPassword(event.target.value)}
                  placeholder="Masukkan password"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") void submitLogin();
                  }}
                />
              </div>
              <Button className="w-full" onClick={() => void submitLogin()} disabled={authBusy}>
                {authBusy ? "Memproses..." : "Login"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 border-r border-sidebar-border bg-sidebar flex-col">
        <div className="px-5 py-5 border-b border-sidebar-border">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Dashboard</div>
            <div className="font-semibold text-sm tracking-tight text-foreground mt-0.5">HR Payroll</div>
            <div className="text-xs text-muted-foreground mt-1 leading-snug">Excel Upload + Telegram Bot</div>
          </div>
        </div>
        <nav className="flex-1 p-3">{renderNavLinks()}</nav>
        <div className="p-4 text-xs text-muted-foreground border-t border-sidebar-border space-y-3">
          <div>v1.0 · Data lokal + API</div>
          <div className="space-y-1">
            <div className="truncate">{currentUser.email}</div>
            <Button variant="outline" size="sm" className="w-full" onClick={logoutLocalAccount}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col">
        <header className="border-b bg-card px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-3 lg:hidden mb-3">
            <Button variant="outline" size="sm" onClick={() => setOpenMobileNav(true)}>
              <Menu className="h-4 w-4 mr-2" />
              Menu
            </Button>
            <div className="text-xs text-muted-foreground truncate">{currentUser.email}</div>
          </div>
          {activeOnboardingPath === "/karyawan" && (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-border bg-warning px-3 py-2.5 text-sm text-warning-foreground">
              <span>Selesaikan data karyawan terlebih dahulu untuk onboarding.</span>
              <Button size="sm" onClick={handleMarkEmployeeStepDone}>
                Selesaikan Onboarding
              </Button>
            </div>
          )}
        </header>
        <div className="flex-1 p-4 sm:p-6">
          <Outlet />
        </div>
      </main>

      <Sheet open={openMobileNav} onOpenChange={setOpenMobileNav}>
        <SheetContent side="left" className="w-[85vw] max-w-xs p-0 flex h-full flex-col">
          <SheetHeader className="px-4 py-4 border-b border-sidebar-border text-left">
            <SheetTitle className="text-base">Menu Payroll</SheetTitle>
          </SheetHeader>
          <div className="px-3 py-3">{renderNavLinks(true)}</div>
          <div className="mt-auto px-4 py-4 border-t border-sidebar-border text-xs text-muted-foreground space-y-3">
            <div className="truncate">{currentUser.email}</div>
            <Button variant="outline" size="sm" className="w-full" onClick={logoutLocalAccount}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}